import type { ItineraryEditor } from "../types";
import type { PendingSummaryRewriteSlots } from "../service/itinerarySummaryService";

export type ItineraryLocalDraft = {
  schemaVersion: 1;
  itineraryId: string;
  baseRevision: string;
  updatedAt: string;
  payload: ItineraryEditor;
  pendingSummaryRewrites?: PendingSummaryRewriteSlots;
  pendingPointRewrites?: Record<string, string[]>;
  forcedSummaryRewrites?: PendingSummaryRewriteSlots;
};

export type ItineraryLocalDraftStore = {
  load: (itineraryId: string) => Promise<ItineraryLocalDraft | null>;
  save: (draft: ItineraryLocalDraft) => Promise<void>;
  remove: (itineraryId: string, expectedDraft?: ItineraryLocalDraft | null) => Promise<void>;
  subscribe?: (itineraryId: string, onChange: (draft: ItineraryLocalDraft | null) => void) => () => void;
};

const storageKey = (itineraryId: string) => `flower-master:itinerary-local-draft:v1:${itineraryId}`;

/** 浏览器本地草稿仓储；坏数据直接隔离，绝不覆盖服务端快照。 */
export const browserItineraryLocalDraftStore: ItineraryLocalDraftStore = {
  async load(itineraryId) {
    let storage: Storage | undefined;
    let raw: string | null;
    try {
      storage = globalThis.localStorage;
      if (!storage) return null;
      raw = storage.getItem(storageKey(itineraryId));
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const value = JSON.parse(raw) as unknown;
      if (!isLocalDraft(value, itineraryId)) {
        storage.removeItem(storageKey(itineraryId));
        return null;
      }
      return value;
    } catch {
      try {
        storage.removeItem(storageKey(itineraryId));
      } catch {
        // 存储被浏览器禁用时只忽略坏草稿，不影响后端加载。
      }
      return null;
    }
  },
  async save(draft) {
    try {
      const storage = globalThis.localStorage;
      if (!storage) throw new Error("浏览器本地草稿存储不可用");
      storage.setItem(storageKey(draft.itineraryId), JSON.stringify(draft));
    } catch (cause) {
      const quotaExceeded = cause instanceof DOMException && cause.name === "QuotaExceededError";
      throw new Error(quotaExceeded
        ? "浏览器本地存储空间不足，草稿尚未保存，当前修改仅保留在本页面。请保留此页面，处理浏览器存储空间后重试保存。"
        : "浏览器本地存储不可用，草稿尚未保存，当前修改仅保留在本页面。请保留此页面，恢复本站点的存储权限后重试保存。", { cause });
    }
  },
  async remove(itineraryId, expectedDraft) {
    try {
      const storage = globalThis.localStorage;
      const key = storageKey(itineraryId);
      // 保存完成只能清理本页记录的快照，其他标签的后续写入必须保留。
      if (expectedDraft !== undefined && storage?.getItem(key) !== JSON.stringify(expectedDraft)) return;
      storage?.removeItem(key);
    } catch {
      // 后端已保存成功时，本地清理失败不应把成功结果改判为失败。
    }
  },
  subscribe(itineraryId, onChange) {
    const notify = () => {
      let draft: ItineraryLocalDraft | null = null;
      try {
        const raw = globalThis.localStorage?.getItem(storageKey(itineraryId));
        const value: unknown = raw ? JSON.parse(raw) : null;
        if (isLocalDraft(value, itineraryId)) draft = value;
      } catch { /* 无法读取备份时不能继续承诺本地已保存。 */ }
      onChange(draft);
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== storageKey(itineraryId)) return;
      try {
        if (event.storageArea !== globalThis.localStorage) return;
      } catch { /* 存储权限被撤回时仍通知页面失效。 */ }
      // 事件可能晚于后续写入到达，始终核对当前缓存，不采用过期的事件载荷。
      notify();
    };
    window.addEventListener("storage", handleStorage);
    // 同时覆盖首次读取与页面采用草稿之间发生的外部更新。
    notify();
    return () => window.removeEventListener("storage", handleStorage);
  },
};

/** 只接受当前行程、有效时间和完整编辑器骨架，防止跨行程或坏 JSON 污染。 */
function isLocalDraft(value: unknown, itineraryId: string): value is ItineraryLocalDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as Partial<ItineraryLocalDraft>;
  const payload = draft.payload as Partial<ItineraryEditor> | undefined;
  return draft.schemaVersion === 1
    && draft.itineraryId === itineraryId
    && typeof draft.baseRevision === "string"
    && typeof draft.updatedAt === "string"
    && Number.isFinite(Date.parse(draft.updatedAt))
    && payload?.id === itineraryId
    && Array.isArray(payload.days)
    && Array.isArray(payload.segments)
    && isStringArrayRecord(draft.pendingPointRewrites)
    && isSummarySlotRecord(draft.pendingSummaryRewrites)
    && isSummarySlotRecord(draft.forcedSummaryRewrites);
}

/** 拒绝损坏的节点队列，避免恢复草稿后执行任意结构。 */
function isStringArrayRecord(value: unknown): boolean {
  return value === undefined || Boolean(value && typeof value === "object" && !Array.isArray(value)
    && Object.values(value).every((items) => Array.isArray(items) && items.every((item) => typeof item === "string")));
}

/** 本地队列接纳分时与全天正文范围，不扩大节点时段契约。 */
function isSummarySlotRecord(value: unknown): boolean {
  return value === undefined || Boolean(value && typeof value === "object" && !Array.isArray(value)
    && Object.values(value).every((slots) => Array.isArray(slots)
      && slots.every((slot) => slot === "morning" || slot === "afternoon" || slot === "evening" || slot === "full_day")));
}
